import { useState } from 'react';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { Card, Heading, Inline, LinkButton, Stack, Text, vars } from '@etg/wings';
import { CaretDown } from '@phosphor-icons/react';
import { ExpandableContent } from '@eti/components';

const contentWrapperStyles = css`
  display: flex;
  flex-direction: column;
  line-height: 140%;
  padding: 16px 0;

  & ul {
    list-style: outside;
    padding-inline-start: 20px;
  }
`;

const readMoreStyles = css`
  display: flex;
  flex-direction: column;
  line-height: 140%;
  padding: 0 16px;

  & ul {
    list-style: outside;
    padding-inline-start: 20px;
  }
`;

const iconStyles = (showMore: boolean) => css`
  color: ${vars.colors.links.default};
  flex-shrink: 0;
  transform: rotateX(${showMore ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;

interface EtgFaresDrawerContentProps {
  description: string | null;
  dimensions: string | null;
  id: number;
  name: string;
  piecesAndWeight: string | null;
  readMoreText?: string | null;
  termsUrl: string | null;
}

const EtgFaresDrawerContent = ({
  description,
  dimensions,
  id,
  name,
  piecesAndWeight,
  readMoreText,
  termsUrl,
}: EtgFaresDrawerContentProps) => {
  const { t } = useTranslation();
  const readMoreButtonText = t('Products.ReadMore.Link.Label');
  const readLessButtonText = t('Products.ReadLess.Link.Label');
  const coverGeniusPolicyLinkText = t('coverGeniusPolicyLinkText');
  const openInNewTabText = t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text');

  const [showMore, setShowMore] = useState(false);
  const readMoreButtonLabel = showMore ? readLessButtonText : readMoreButtonText;
  const handleClick = () => {
    setShowMore((state) => !state);
  };

  const termsLink = (
    <Inline>
      <a
        aria-label={`${coverGeniusPolicyLinkText} (${openInNewTabText})`}
        href={termsUrl ?? undefined}
        rel="noreferrer"
        target="_blank"
      >
        {coverGeniusPolicyLinkText}
      </a>
    </Inline>
  );

  return (
    <Card>
      <Heading data-testid="content-title" level={3}>
        <bdi>
          {name}
          {piecesAndWeight && ` (${piecesAndWeight})`}
        </bdi>
      </Heading>
      <Text asHtml className={contentWrapperStyles}>
        {description}
      </Text>
      <Stack spacing={4}>
        {piecesAndWeight && <Text as="bdi">{piecesAndWeight}</Text>}
        {dimensions && <Text as="bdi">{dimensions}</Text>}
        {termsUrl && !readMoreText && termsLink}
        {readMoreText && !piecesAndWeight && (
          <>
            <Inline>
              <LinkButton
                aria-controls={`read-more-${id}`}
                aria-expanded={showMore}
                aria-label={`${readMoreButtonLabel} ${name}`}
                data-testid="readMore-button"
                onClick={handleClick}
              >
                <Inline noWrap spacing={4}>
                  {readMoreButtonLabel}
                  <CaretDown
                    aria-hidden
                    className={iconStyles(showMore)}
                    size={20}
                    weight="light"
                  />
                </Inline>
              </LinkButton>
            </Inline>
            <ExpandableContent isExpanded={showMore}>
              <Text asHtml className={readMoreStyles} id={`read-more-${id}`}>
                {readMoreText}
              </Text>
              {termsUrl && termsLink}
            </ExpandableContent>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default EtgFaresDrawerContent;
